import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { de } from "date-fns/locale"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { buttonVariants } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "TT.MM.JJJJ",
  className,
  disabled = false,
  ...props 
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const containerRef = React.useRef(null)
  const inputRef = React.useRef(null)

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Convert string date (YYYY-MM-DD) to Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    try {
      const parts = value.split('-')
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10)
        const day = parseInt(parts[2], 10)
        if (year && month && day && year >= 1900 && year <= 2100) {
          return new Date(year, month - 1, day)
        }
      }
      return undefined
    } catch {
      return undefined
    }
  }, [value])

  // Sync input value with date value
  React.useEffect(() => {
    if (dateValue) {
      setInputValue(format(dateValue, "dd.MM.yyyy"))
    } else if (!value) {
      setInputValue('')
    }
  }, [dateValue, value])

  // Convert Date object back to string (YYYY-MM-DD)
  const handleSelect = (date) => {
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      onChange(`${year}-${month}-${day}`)
    } else {
      onChange('')
    }
    setIsOpen(false)
  }

  // Handle text input
  const handleInputChange = (e) => {
    const val = e.target.value
    setInputValue(val)
    
    // Try to parse the date (supports dd.mm.yyyy, dd/mm/yyyy, dd-mm-yyyy)
    const cleanVal = val.replace(/[\/\-]/g, '.')
    const parts = cleanVal.split('.')
    
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      
      // Validate and convert
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        onChange(dateStr)
      }
    }
  }

  // Handle blur - format the input
  const handleInputBlur = () => {
    if (dateValue) {
      setInputValue(format(dateValue, "dd.MM.yyyy"))
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "rounded-none h-11 pr-10",
            className
          )}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-0 top-0 h-11 w-10 p-0 hover:bg-transparent"
        >
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-[9999] bg-popover border border-border rounded-md shadow-lg">
          <div className="flex items-center justify-between p-2 border-b">
            <span className="text-sm font-medium">Datum wählen</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <DayPicker
            mode="single"
            selected={dateValue}
            onSelect={handleSelect}
            defaultMonth={dateValue || new Date()}
            showOutsideDays
            className="p-3"
            classNames={{
              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              month: "space-y-4",
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "text-sm font-medium",
              nav: "space-x-1 flex items-center",
              nav_button: cn(
                buttonVariants({ variant: "outline" }),
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
              ),
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse space-y-1",
              head_row: "flex",
              head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
              row: "flex w-full mt-2",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
              day: cn(
                buttonVariants({ variant: "ghost" }),
                "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
              ),
              day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
              day_today: "bg-accent text-accent-foreground",
              day_outside: "text-muted-foreground opacity-50",
              day_disabled: "text-muted-foreground opacity-50",
              day_hidden: "invisible",
            }}
            components={{
              IconLeft: () => <ChevronLeft className="h-4 w-4" />,
              IconRight: () => <ChevronRight className="h-4 w-4" />,
            }}
          />
        </div>
      )}
    </div>
  )
}
